import React, { useState } from 'react';

import _ from 'lodash';
import PropTypes from 'prop-types';
import { Alert, AlertTitle } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import readXlsxFile from 'read-excel-file';
import { toast } from 'react-toastify';
import { Button, Modal, FormControl, InputLabel, Select, MenuItem, Typography } from '@material-ui/core';
import moment from 'moment';

import { IsEmpty, IfEmptyThrowError, IfNotEmptyThrowError, IfFalseThrowError } from '../../../common/checks';
import { usePromoCode } from '../../../hook/usePromoCode';
import MText from '../../common/MText';
import Images from '../../basics/Images';

const useStyles = makeStyles((theme) => ({
  paper: {
    position:        'absolute',
    width:           550,
    top:             '50%',
    left:            '50%',
    transform:       'translate(-50%, -50%)',
    backgroundColor: theme.palette.background.paper,
    borderRadius:    '12px',
    boxShadow:       theme.shadows[5],
    padding:         theme.spacing(2, 4, 3),
  },
}));

export default function CreateModal({ open, onClose }) {
  const classes = useStyles();

  const { gifts, outlets, onPromoCodeUpload } = usePromoCode();
  const [giftId, setGiftId] = useState('');
  const [fileName, setFileName] = useState('');
  const [outletId, setOutletId] = useState('');
  const [promoCodes, setPromoCodes] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  const onModalClose = () => {
    setErrorMessage('');
    setGiftId('');
    setOutletId('');
    setPromoCodes([]);
    setFileName('');
    onClose();
  };

  /**
   * Special function for parsing date from the xlsx file
   * If it is a string, it must be in DD/MM/YYYY format
   * It returns a moment object
   */
  const parseDate = (input) => {
    // Check if the input is a Date object
    if (Object.prototype.toString.call(input) === '[object Date]') {
      return moment(input);
    } else if (typeof input === 'string') {
      return moment(input, 'DD/MM/YYYY', true);
    } else {
      throw new Error('Invalid date input');
    }
  };

  const onFileUpload = async (file) => {
    try {
      const codesArray = [];

      setFileName(file.name || '');
      const codes = await readXlsxFile(file)
        .then((rows) => {
          const code = [];

          _.map(rows, (r, index) => {
            const rowNumber = index + 1;

            IfEmptyThrowError(r[0], `empty promo code on row ${rowNumber}`);
            IfEmptyThrowError(r[1], `empty expiry date on row ${rowNumber}`);
            IfFalseThrowError(parseDate(r[1]).isValid(), `Invalid expiry date on row ${rowNumber}`);

            codesArray.push(r[0]);
            code.push({ code: r[0].toString(), expiryDate: parseDate(r[1]).utcOffset('+08').endOf('day') });
          });

          return code;
        }).catch((err) => {
          throw err;
        });

      // check for duplicate promoCode
      const countCode = _.countBy(codesArray);
      const duplicates = _.keys(_.pickBy(countCode, (count) => count > 1));
      IfNotEmptyThrowError(duplicates, `Promo code ${duplicates.join(',')} are duplicate`);
      setPromoCodes(codes);
    } catch (err) {
      setPromoCodes([]);
      toast.error(err.message);
    }
  };

  const onSubmitPromoCode = async (e) => {
    try {
      e.preventDefault();

      const data = {
        giftId,
        promoCodes: JSON.stringify(promoCodes),
      };

      if (!IsEmpty(outletId)) {
        data.outletId = outletId;
      }

      await onPromoCodeUpload(data);
      onModalClose();
      toast.success('Promo codes uploaded sucessfully');
    } catch (err) {
      setErrorMessage(err.message);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onModalClose}
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
    >
      <div className={classes.paper}>
        <Typography variant="h5">Upload promo codes</Typography>
        <form onSubmit={onSubmitPromoCode}>
          <FormControl className="w-100 my-3">
            <InputLabel id="demo-simple-select-label">Gift ID</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={giftId}
              onChange={(e) => setGiftId(e.target.value)}
            >
              {
                IsEmpty(gifts) ? (
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                )
                  : _.map(gifts, (gift) => (
                    <MenuItem value={gift.id}>
                      {gift.label}
                    &nbsp; - &nbsp;
                      {gift.id}
                    </MenuItem>
                  ))
              }
            </Select>
          </FormControl>

          <Alert className="my-2" severity="info">
            <AlertTitle>Promo code upload guideline</AlertTitle>
            <ul className="pl-0">
              <li>
                Uploaded file format should be&nbsp;
                <strong>.xlsx</strong>
              </li>
              <li>
                All promo code should be in 1 sheet&nbsp;
                <strong>(not mutiple sheet)</strong>
              </li>
              <li>Promo code should be started from first row and column</li>
              <li>Expiry date should be placed next to promo code</li>
              <li>
                Expiry date format should follow&nbsp;
                <strong>DD/MM/YYYY</strong>
              </li>
            </ul>
          </Alert>
          <div className="w-100 my-2 d-flex flex-column justify-content-center align-items-center">
            <MText text="Sample file data" />
            <img
              src={Images.Local.PromoCodesSample}
              alt="sample promo codes"
              style={{ height: '80px', width: '200px'  }}
            />
          </div>
          <div className="w-100 mt-3 my-1 d-flex justify-content-center">
            <input
              accept=".xlsx, .xls"
              className="d-none"
              id="contained-button-file"
              type="file"
              onChange={(e) => onFileUpload(e.target.files[0])}
            />
            <div className="d-flex flex-row align-items-center">
              <label htmlFor="contained-button-file">
                <Button variant="contained" color="primary" component="span">
                  Select file
                </Button>
              </label>
            </div>
          </div>
          <div className="w-100 d-flex flex-column align-items-center">
            <MText bold text={fileName} />
          </div>

          <FormControl className="w-100">
            <InputLabel id="demo-simple-select-label">Outlet</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={outletId}
              onChange={(e) => setOutletId(e.target.value)}
            >
              {
                IsEmpty(outlets) ? (
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                )
                  : _.map(outlets, (outlet) => (
                    <MenuItem value={outlet.id}>{outlet.name}</MenuItem>
                  ))
              }
            </Select>
          </FormControl>

          {
            !IsEmpty(errorMessage) && (
              <Alert className="my-2 overflow-auto" severity="error" style={{ maxHeight: '80px' }}>
                <AlertTitle>
                  {errorMessage.split('.')[0]}
                  .
                </AlertTitle>
                {errorMessage.split('.').length > 1 ? errorMessage.split('.')[1] : ''}
              </Alert>
            )
          }

          <div className="w-100 mt-3 d-flex justify-content-center">
            {
              !IsEmpty(promoCodes) && (
                <Button variant="contained" color="primary" type="submit">Upload</Button>
              )
            }
          </div>
        </form>
      </div>
    </Modal>
  );
}

CreateModal.propTypes = {
  open:    PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
